import { Button, Card, colors, FlexBox, Modal, Select, Table, TextField } from '@cimpress/react-components';
import { faBackward, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, ReactNode, useState } from 'react';
import toast from 'react-hot-toast';
import { CustomPattern, customPatternStrategies, CustomPatternStrategy, EventMapping } from '../../clients/dto/VLRATypes';
import { deleteCustomPattern, saveCustomPattern } from '../../clients/VLRAClient';
import { buttonDanger } from '../../types/button-styles';
import { css } from '@emotion/css';
import { SelectOption } from '../../types/select';
import TypeaheadTextField from '../common/TypeaheadTextField';
import { MONITOR_EVENTS, PRINT_JOB_STATUSES } from './util/statuses';

const textFieldInTable = css`
  margin-bottom: 0;
`;

export interface CustomPatternVariables {
    gang?: number;
    itn?: string;
    unit?: number;
    side?: string;
    layoutId?: string;
}

const variablesPerStrategy: Record<CustomPatternStrategy, (keyof CustomPatternVariables)[]> = {
  'Unit': ['itn', 'unit', 'side'],
  'Gang': ['gang', 'itn', 'side'],
  'Nest': ['layoutId', 'side'],
};

const ParseStringVariableFromRegex = (
    regexResult: RegExpExecArray | null,
    key: keyof CustomPatternVariables,
): string | undefined => {
    return regexResult?.groups?.[key] ? regexResult.groups[key] : undefined;
};

const ParseNumberVariableFromRegex = (
    regexResult: RegExpExecArray | null,
    key: keyof CustomPatternVariables,
): number | undefined => {
    const stringValue = ParseStringVariableFromRegex(regexResult, key);
    return stringValue ? Number.parseInt(stringValue) : undefined;
};

type EventMappingRow = {
  em: EventMapping,
  index: number,
};

type EventMappingTableColumn = {
  Header: string,
  accessor: keyof EventMapping | '',
  Cell?: (row: {original: EventMappingRow}) => ReactNode,
};

type ExampleRow = {
  filename: string,
  variables: CustomPatternVariables,
  index: number,
};

type ExampleTableColumn = {
  Header: string | ReactNode,
  accessor: 'filename' | '',
  Cell?: (row: {original: ExampleRow}) => ReactNode,
};

export function EditCustomPatternDisplay({initialPattern, isNewPattern, onClose} : {initialPattern: CustomPattern, isNewPattern: boolean, onClose: () => void}): ReactElement {

  // UI values
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [savedPattern, setSavedPattern] = useState(initialPattern);
  const [strategyOptions] = useState(customPatternStrategies.map(strat => ({label: strat, value: strat})) as SelectOption[]);

  // Pattern properties
  const [name, setName] = useState(initialPattern.name);
  const [filenameRegex, setFilenameRegex] = useState(initialPattern.filenameRegex);
  const [priority, setPriority] = useState(initialPattern.priority);
  const [strategy, setStrategy] = useState(initialPattern.strategy);
  const [eventMapping, setEventMapping] = useState(initialPattern.eventMapping ?? []);
  const [examples, setExamples] = useState(initialPattern.examples ?? []);
  const [backsideEnabledExpression, setBacksideEnabledExpression] = useState(initialPattern.backsideProcessing?.isEnabledExpression);

  const onDelete = () => {
    toast.promise(deleteCustomPattern(initialPattern), {
      loading: <>Deleting &apos;{initialPattern.name}&apos; pattern</>,
      error: (error) =><strong>Failed to delete pattern. Error: {error.toString()}</strong>,
      success: <strong>Deleted &apos;{initialPattern.name}&apos; pattern</strong>
    })
    .then(onClose);
  };

  const getCurrentPattern = (): CustomPattern => {
    const backside = backsideEnabledExpression ? {
      isEnabledExpression: backsideEnabledExpression,
    } : undefined;

    return {
      ...initialPattern,
      name,
      filenameRegex,
      priority,
      eventMapping,
      strategy,
      examples,
      backsideProcessing: backside
    };
  };

  const onSave = () => {
    const newPattern = getCurrentPattern();
    toast.promise(saveCustomPattern(newPattern), {
      loading: <>Saving &apos;{newPattern.name}&apos; pattern</>,
      error: (error) =><strong>Failed to save pattern. Error: {error.toString()}</strong>,
      success: <strong>Saved &apos;{newPattern.name}&apos; pattern</strong>
    }).then(() => setSavedPattern(newPattern));
  };

  const editExample = (index: number, value: string | undefined) => {
    const newExamples: (string | undefined)[] = [...examples];
    newExamples[index] = value;
    setExamples(newExamples.filter(example => example !== undefined) as string[]);
  };

  const editEventMapping = (index: number, value: EventMapping | undefined) => {
    const newMappings: (EventMapping | undefined)[] = [...eventMapping];
    newMappings[index] = value;
    setEventMapping(newMappings.filter(example => example !== undefined) as EventMapping[]);
  };

  const getExampleValues = (example: string) => {
    let regexResult: RegExpExecArray | null = null;
    try {
      regexResult = new RegExp(filenameRegex).exec(example);
    }
    catch (error) {
      //Ignore regex errors while they type
    }
    return {
      gang: ParseNumberVariableFromRegex(regexResult, 'gang'),
      itn: ParseStringVariableFromRegex(regexResult, 'itn'),
      side: ParseStringVariableFromRegex(regexResult, 'side'),
      unit: ParseNumberVariableFromRegex(regexResult, 'unit'),
      layoutId: ParseStringVariableFromRegex(regexResult, 'layoutId'),
    };
  };

  const eventMappingColumns: EventMappingTableColumn[] = [
    { 
      Header: 'Input Event',
      accessor: 'inputEvent',
      Cell: ({original: {em, index}}) => <TypeaheadTextField name={`mapping.inputEvent${index}`} key={index} value={em.inputEvent} onChange={newValue => editEventMapping(index, {...em, inputEvent: newValue})} className={textFieldInTable} options={MONITOR_EVENTS}/> 
    },
    { 
      Header: 'Output Event/Status', 
      accessor: 'outputEventOrStatus',
      Cell: ({original: {em, index}}) => <TypeaheadTextField name={`mapping.outputEventOrStatus${index}`} key={index} value={em.outputEventOrStatus} onChange={newValue => editEventMapping(index, {...em, outputEventOrStatus: newValue})} className={textFieldInTable} options={PRINT_JOB_STATUSES}/>
     },
    { Header: 
      'Half-processed Output Event/Status', 
      accessor: 'halfProcessedEventOrStatus' ,
      Cell: ({original: {em, index}}) => <TypeaheadTextField name={`mapping.halfProcessedEventOrStatus${index}`} key={index} value={em.halfProcessedEventOrStatus} onChange={newValue => editEventMapping(index, {...em, halfProcessedEventOrStatus: newValue})} className={textFieldInTable} options={PRINT_JOB_STATUSES}/>
    },
    { 
      Header: '', 
      accessor: '' ,
      Cell: ({original: {index}}) => <Button className={buttonDanger} onClick={() => editEventMapping(index, undefined)}>Delete</Button>
    },
  ];

  const getExampleColumns = () => {
    const exampleColumns: ExampleTableColumn[] = [
      { 
        Header: 'Filename',
        accessor: 'filename',
        Cell: ({original: {filename, index}}) => <TextField name={`example.filename${index}`} key={index} value={filename} onChange={e => editExample(index, e.target.value)} className={textFieldInTable}/> 
      }
    ];

    if (variablesPerStrategy[strategy]?.includes('gang')) {
      exampleColumns.push({
        Header: <>Gang # <code style={{textTransform: 'initial'}}>(?&lt;gang&gt;...)</code></>,
        accessor: '',
        Cell: ({original: {variables}}) => <code>{variables.gang}</code>
      });
    }

    if (variablesPerStrategy[strategy]?.includes('itn')) {
      exampleColumns.push({
        Header: <>ITN <code style={{textTransform: 'initial'}}>(?&lt;itn&gt;...)</code></>,
        accessor: '',
        Cell: ({original: {variables}}) => <code>{variables.itn}</code>
      });
    }

    if (variablesPerStrategy[strategy]?.includes('unit')) {
      exampleColumns.push({
        Header: <>Unit # <code style={{textTransform: 'initial'}}>(?&lt;unit&gt;...)</code></>,
        accessor: '',
        Cell: ({original: {variables}}) => <code>{variables.unit}</code>
      });
    }

    if (variablesPerStrategy[strategy]?.includes('layoutId')) {
      exampleColumns.push({
        Header: <>Layout ID <code style={{textTransform: 'initial'}}>(?&lt;layoutId&gt;...)</code></>,
        accessor: '',
        Cell: ({original: {variables}}) => <code>{variables.layoutId}</code>
      });
    }

    if (variablesPerStrategy[strategy]?.includes('side')) {
      exampleColumns.push({
        Header: <>Side <code style={{textTransform: 'initial'}}>(?&lt;side&gt;...)</code></>,
        accessor: '',
        Cell: ({original: {variables}}) => <code>{variables.side}</code>
      });
    }

    exampleColumns.push({ 
      Header: '', 
      accessor: '' ,
      Cell: ({original: {index}}) => <Button className={buttonDanger} onClick={() => editExample(index, undefined)}>Delete</Button>
    });

    return exampleColumns;
  };

  const header = <FlexBox>
    <h3>{savedPattern.name ?? 'New Pattern'}</h3>
    <div>
      {!isNewPattern && <Button variant='secondary' onClick={() => setShowDeletePrompt(true)} className={`me-3 + ${buttonDanger}`}><FontAwesomeIcon icon={faTrash}/> Delete</Button>}
      <Button variant='primary' onClick={onSave}><FontAwesomeIcon icon={faSave}/> Save</Button>
    </div>
  </FlexBox>;

  return (
    <div className='container mb-3'>
      <Modal
        status="danger"
        show={showDeletePrompt}
        onRequestHide={() => setShowDeletePrompt(false)}
        title="Delete Pattern"
        closeButton={true}
        footer={(
          <div className="button-group">
            <Button onClick={() => setShowDeletePrompt(false)}>
              Cancel
            </Button>
            <Button onClick={onDelete} variant='secondary' className={buttonDanger}>
              Delete
            </Button>
          </div>
        )}
      >
        Are you you want to delete the <strong>{name}</strong> pattern? 
      </Modal>
      <Button variant='link' onClick={onClose} className='mb-3'><FontAwesomeIcon icon={faBackward}/> Return To All Patterns</Button>
      
      <Card className='ms-3 mb-3' header={header} >
        <h3>Properties</h3>
        <TextField name='name' label='Name' value={name} onChange={e => setName(e.target.value)}/>
        <TextField name='priority' label='Priority' type="number" step={1} value={priority} onChange={e => setPriority(Number.parseInt(e.target.value))}/>
        <Select name='strategy' label='Strategy' value={strategyOptions.find(so => so.value === strategy)} options={strategyOptions} onChange={e => setStrategy(e.value)}/>
        <TextField name='backsideEnabledExpression' label='Backside Enabled Expression' value={backsideEnabledExpression} onChange={e => setBacksideEnabledExpression(e.target.value)}/>
        <hr/>
        <h3>Events</h3>
        <Table columns={eventMappingColumns} data={eventMapping.map((em, index) => ({em, index} as EventMappingRow))} />
        <Button onClick={() => editEventMapping(eventMapping.length, {inputEvent: '', outputEventOrStatus: '', halfProcessedEventOrStatus: ''})}>Add New Mapping</Button>
        <div className='mt-3'><FontAwesomeIcon icon={'info-circle'} color={colors.info.base}/><small> Known input events and print job statuses will be suggested, but you can use freeform text to handle and publish custom events.</small></div>
        <hr/>
        <h3>Regex</h3>
        <TextField name='filenameRegex' label='Filename Regex' value={filenameRegex} onChange={e => setFilenameRegex(e.target.value)}/> 
        <div className='mt-3'>
          <FontAwesomeIcon icon={'info-circle'} color={colors.info.base}/> <small> 
            Custom patterns use ECMAScript/Javascript regex logic to detect matching filenames and uses
            the <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Named_capturing_group" target='_blank' rel="noreferrer">Named Capturing Group</a> feature to parse variables like the gangNbr from the filename.
            For example: <code>{'(?<gang>\\d{9,13})(?<side>[F,B])'}</code> will grab the gang and side from the filenames.
            <br />
            Regexes are tricky and can require a bit of learning. We recommend using <a href='https://regex101.com/r/TyTcdp/2' target='_blank' rel="noreferrer">Regex101</a> for practicing with them.
          </small>
        </div>

        <hr/>
        <h3>Examples</h3>
        <Table columns={getExampleColumns()} data={examples.map((filename, index) => ({filename, index, variables: getExampleValues(filename) } as ExampleRow))} />
        <Button onClick={() => editExample(examples.length, '')}>Add New Example</Button>
      </Card>
    </div>
  );
}
